import { createContext, useContext, useState, useEffect } from "react";
import {
  browserLocalPersistence,
  getIdTokenResult,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {
  auth,
  googleProvider,
  appleProvider,
  isFirebaseConfigured,
  missingFirebaseEnvKeys,
} from "../firebase";
import { isCreatorEmail } from "../utils/authRoles";

const AuthContext = createContext();
const LOCAL_OAUTH_HOST_REDIRECTS = new Set(["127.0.0.1", "0.0.0.0"]);

function redirectLocalOAuthHostIfNeeded() {
  if (typeof window === "undefined") {
    return false;
  }

  const { hostname, protocol, port, pathname, search, hash } = window.location;
  if (!LOCAL_OAUTH_HOST_REDIRECTS.has(hostname)) {
    return false;
  }

  const targetUrl = `${protocol}//localhost${port ? `:${port}` : ""}${pathname}${search}${hash}`;
  window.location.replace(targetUrl);
  return true;
}

function getFirebaseConfigError() {
  const missingKeys = missingFirebaseEnvKeys.join(", ");
  return new Error(
    `Firebase auth is not configured.${missingKeys ? ` Missing: ${missingKeys}` : ""}`
  );
}

function getGoogleSignInError(error) {
  switch (error?.code) {
    case "auth/popup-blocked":
      return new Error("Popup was blocked. Allow popups and try again.");
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return new Error("Google sign-in was canceled before completion.");
    case "auth/unauthorized-domain":
      return new Error(
        "This domain is not authorized for Firebase Auth. Add it in Firebase Auth > Settings > Authorized domains."
      );
    case "auth/operation-not-allowed":
      return new Error(
        "Google sign-in is not enabled for this Firebase project. Enable Google in Firebase Auth > Sign-in method."
      );
    default:
      return error instanceof Error
        ? error
        : new Error("Google sign-in failed. Please try again.");
  }
}

async function enrichUser(currentUser) {
  if (!currentUser) return null;

  const tokenResult = await getIdTokenResult(currentUser).catch(() => null);
  const claims = tokenResult?.claims || {};
  const claimRoles = Array.isArray(claims.roles) ? claims.roles : [];
  const claimRole = typeof claims.role === "string" ? claims.role : null;
  const normalizedRoles = [...claimRoles, claimRole]
    .filter(Boolean)
    .map((role) => String(role).toLowerCase());
  const email = currentUser.email?.trim().toLowerCase();
  const allowlistedCreator = isCreatorEmail(email);
  const isAdmin = claims.isAdmin === true || normalizedRoles.includes("admin");
  const isCreator =
    isAdmin ||
    claims.isCreator === true ||
    normalizedRoles.includes("creator") ||
    allowlistedCreator;

  return {
    ...currentUser,
    claims,
    customClaims: claims,
    role: claimRole || (isCreator ? "creator" : "consumer"),
    roles: normalizedRoles.length > 0 ? normalizedRoles : isCreator ? ["creator"] : ["consumer"],
    isCreator,
    isAdmin,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return undefined;
    }

    setPersistence(auth, browserLocalPersistence).catch(() => {
      // Persistence failure should not block auth flows.
    });

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      const resolvedUser = await enrichUser(currentUser);
      setUser(resolvedUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  async function signInWithGoogle() {
    if (redirectLocalOAuthHostIfNeeded()) {
      return new Promise(() => {});
    }

    if (!auth || !isFirebaseConfigured) {
      throw getFirebaseConfigError();
    }

    try {
      return await signInWithPopup(auth, googleProvider);
    } catch (error) {
      throw getGoogleSignInError(error);
    }
  }

  async function signInWithApple() {
    if (redirectLocalOAuthHostIfNeeded()) {
      return new Promise(() => {});
    }

    if (!auth || !isFirebaseConfigured) {
      throw getFirebaseConfigError();
    }

    try {
      return await signInWithPopup(auth, appleProvider);
    } catch (error) {
      throw getGoogleSignInError(error);
    }
  }

  function signUpWithEmail(email, password) {
    if (!auth || !isFirebaseConfigured) {
      throw getFirebaseConfigError();
    }

    return createUserWithEmailAndPassword(auth, email, password);
  }

  function signInWithEmail(email, password) {
    if (!auth || !isFirebaseConfigured) {
      throw getFirebaseConfigError();
    }

    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    if (!auth || !isFirebaseConfigured) {
      throw getFirebaseConfigError();
    }

    return signOut(auth).then(() => {
      setUser(null);
    });
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signInWithApple,
        signUpWithEmail,
        signInWithEmail,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
